'use client'

import { useEffect } from 'react'
import { useAuthStore } from '@/lib/store/auth-store'
import type { User } from '@/lib/store/auth-store'

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001/api'

// Antes este componente recibía `user` ya resuelto por el layout raíz
// (getServerUser(), server-side). Eso obligaba a leer cookies() en CADA
// página del sitio, lo que a su vez forzaba a Next.js a renderizar TODO el
// sitio dinámicamente (nada de HTML estático servido desde el CDN de
// Netlify) — incluidas páginas 100% estáticas como /terminos o /privacidad,
// que quedaban expuestas al cold start de la función serverless en cada
// visita poco frecuente. Ahora el chequeo de sesión corre acá, del lado del
// cliente, después del mount — el layout raíz ya no depende de cookies() y
// las páginas sin datos dinámicos propios pueden pre-generarse como estáticas.
export function AuthHydrator() {
  const setUser = useAuthStore((state) => state.setUser)

  useEffect(() => {
    let cancelled = false

    fetch(`${API_URL}/auth/me`, { credentials: 'include' })
      .then(async (res) => {
        if (res.ok) return res.json()
        // Solo un 401 real significa "no hay sesión" — cualquier otra falla
        // (502/503 durante un redeploy del backend, timeout, red caída) es
        // un problema transitorio del servidor, no una sesión inválida. Antes
        // esto limpiaba el usuario ante CUALQUIER error, lo que hacía parecer
        // que "se cerraba la sesión" cada vez que se hacía un deploy y se
        // recargaba la página justo en la ventana en que el backend viejo ya
        // había bajado y el nuevo todavía no respondía.
        if (res.status === 401) return null
        return undefined
      })
      .then((data: (User & { _id: string }) | null | undefined) => {
        if (cancelled || data === undefined) return
        setUser(data ? { ...data, id: data._id } : null)
      })
      .catch(() => {
        // Error de red (fetch nunca llegó a tener respuesta) — mismo criterio:
        // no asumir "sesión cerrada", solo dejar de bloquear a quien esperaba
        // `checked` en true.
      })

    return () => {
      cancelled = true
    }
  }, [setUser])

  return null
}
